import { useEffect, useState } from "react";
import { ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

const MAX_PHOTO_BYTES = 3 * 1024 * 1024;
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

export const InventoryPhoto = ({ item, editable = false, onChanged, compact = false }) => {
  const [src, setSrc] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const hasPhoto = Boolean(item?.has_photo);

  useEffect(() => {
    if (!item?.id || !hasPhoto) {
      setSrc("");
      return undefined;
    }
    let active = true;
    let objectUrl = "";
    api
      .get(`/inventory/${item.id}/photo`, { responseType: "blob" })
      .then(({ data }) => {
        objectUrl = URL.createObjectURL(data);
        if (active) setSrc(objectUrl);
      })
      .catch(() => {
        if (active) setSrc("");
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item?.id, hasPhoto, version]);

  if (compact && !hasPhoto) return null;

  const selectPhoto = (event) => {
    const selected = event.target.files?.[0] || null;
    if (!selected) return;
    if (!PHOTO_TYPES.includes(selected.type)) {
      toast.error("Choose a JPEG, PNG or WebP photo");
      event.target.value = "";
      return;
    }
    if (selected.size > MAX_PHOTO_BYTES) {
      toast.error("Photos must be 3 MB or smaller");
      event.target.value = "";
      return;
    }
    setFile(selected);
  };

  const upload = async () => {
    if (!file || !item?.id) return;
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.put(`/inventory/${item.id}/photo`, formData);
      setFile(null);
      setVersion((current) => current + 1);
      onChanged?.(true);
      toast.success("Inventory photo uploaded");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!item?.id) return;
    setBusy(true);
    try {
      await api.delete(`/inventory/${item.id}/photo`);
      setSrc("");
      setFile(null);
      onChanged?.(false);
      toast.success("Inventory photo removed");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={compact ? "shrink-0" : "space-y-3"} data-testid="inventory-photo-section">
      {src ? (
        <img
          alt={item?.name ? `${item.name} inventory item` : "Inventory item"}
          className={
            compact
              ? "h-11 w-11 rounded-md border object-cover"
              : "aspect-[4/3] w-full rounded-lg border bg-muted object-contain"
          }
          src={src}
          data-testid="inventory-photo-image"
        />
      ) : !compact ? (
        <div
          className="grid aspect-[4/3] place-items-center rounded-lg border border-dashed bg-muted/30 text-sm text-muted-foreground"
          data-testid="inventory-photo-empty"
        >
          No photo uploaded
        </div>
      ) : null}
      {editable && item?.id && (
        <div className="space-y-2">
          <Label htmlFor="inventory-photo-file-input">Item photo</Label>
          <Input
            accept="image/jpeg,image/png,image/webp"
            id="inventory-photo-file-input"
            onChange={selectPhoto}
            type="file"
            data-testid="inventory-photo-file-input"
          />
          <p className="text-xs text-muted-foreground" data-testid="inventory-photo-size-help">
            JPEG, PNG or WebP, up to 3 MB.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={!file || busy}
              onClick={upload}
              size="sm"
              type="button"
              data-testid="inventory-photo-upload-button"
            >
              <ImagePlus className="mr-2 h-4 w-4" />
              {busy ? "Uploading…" : hasPhoto ? "Replace photo" : "Upload photo"}
            </Button>
            {hasPhoto && (
              <Button
                disabled={busy}
                onClick={remove}
                size="sm"
                type="button"
                variant="outline"
                data-testid="inventory-photo-remove-button"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Remove photo
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};